import {makeAutoObservable, runInAction} from 'mobx'
import {Lang} from 'yougile-lang'
import {ObjUtil, Time, TypeUtil, Util} from 'yougile-util'
import {
  createDataObjectId,
  DataObjectId,
  Diff,
  Hub,
  HubJSON,
  HubUtil,
  JSONLikeValue,
  taskTemplatesContainerId,
  TimerStickerEntry
} from 'yougile-model'
import {Sync} from '@client/sync'
import {TaskTemplateClient} from '@client/task-template-client'
import {boardSettingsStore} from '@components/board/board-settings/board-settings-store'
import {MessageBox} from '@components/message-box'
import {createUseStore} from '@containers/hooks'
import {TaskChatTab} from '@extensions/app-persistence'
import {ChatSubscribers} from '@extensions/chat-subscribers'
import {globals} from '@extensions/globals-base'
import {Sticker} from '@extensions/sticker/sticker'
import {taskPhantomObserver} from '@extensions/task-phantom-observer'
import {filterCustomStickers} from '@extensions/task-templates/filter-custom-stickers'
import {recreateSubtasksForTaskTemplate} from '@extensions/task-templates/recreate-subtasks-for-task-template'
import {updateShiftDeadline} from './update-shift-deadline'

interface State {
  rows: TaskTemplateRow[] | undefined
  page: 'main' | 'create' | 'edit' | 'existence'
  editId?: DataObjectId
  taskChatTab: TaskChatTab
  errorHasChanges?: boolean
  errorHasChangesBgRed?: boolean
  createFromExist?: boolean
  title?: string
}

export interface TaskTemplateRow {
  id: DataObjectId
  enabled: boolean
  text: string
}
const getInitValue = (): State => ({
  rows: undefined,
  page: 'main',
  taskChatTab: 'chat'
})

export class TaskTemplatesStore {
  state: State = getInitValue()
  companyId: DataObjectId
  boardId: DataObjectId
  template?: Hub
  origin?: HubJSON
  originSubtask: {[subtaskId: DataObjectId]: HubJSON} = {}

  constructor(companyId: DataObjectId, boardId: DataObjectId) {
    makeAutoObservable(this, {}, {autoBind: true})

    this.companyId = companyId
    this.boardId = boardId
  }

  public reset(): void {
    this.template = undefined
    this.origin = undefined
    this.originSubtask = {}
    this.state = getInitValue()

    runInAction(() => {
      boardSettingsStore.setHasChanges(false)
      boardSettingsStore.setOnHasChangesCb(undefined)
    })
  }

  public resetAfterCancel(): void {
    if (this.template && this.origin) {
      const originTemplate = this.origin
      this.template.title = originTemplate.title
      this.template.data = Util.clone(originTemplate.data || {})
      this.template.description = originTemplate.description || ''
      this.template.stickers = Util.clone(originTemplate.stickers || {})

      const box = Sync.getCurrentSandbox()
      for (const subtaskId of getSubtaskIds(this.template)) {
        const subtask = box.getHub(subtaskId)
        if (!subtask) {
          continue
        }

        const origSubtask = this.originSubtask[subtaskId]
        if (!origSubtask) {
          continue
        }

        subtask.deleted = !!origSubtask.deleted
        subtask.title = origSubtask.title
        subtask.data = Util.clone(origSubtask.data || {})
        subtask.description = origSubtask.description || ''
        subtask.stickers = Util.clone(origSubtask.stickers || {})
      }
    }
  }

  public goToMain(): void {
    this.reset()
    this.state.page = 'main'
  }

  public goToExistence(): void {
    this.reset()
    this.state.page = 'existence'
  }

  public cancel(): boolean {
    if (this.canSubmit()) {
      this.showError()
      return true
    }

    this.goToMain()

    return true
  }

  public cancelForce(): boolean {
    this.resetAfterCancel()

    this.goToMain()

    return true
  }

  public goToCreate(): void {
    this.state.page = 'create'

    const hub = new Hub(globals.getCompanyId(), '')
    hub.id = createDataObjectId()
    hub.title = hub.id
    hub.location = taskTemplatesContainerId
    hub.data ??= {}

    hub.data.taskTemplate = {
      id: hub.id,
      boardId: this.boardId,
      title: '',
      enabled: true,
      subscribeUsers: []
    }

    this.template = hub
    const box = Sync.getCurrentSandbox()
    box?.addPhantom(this.template, taskTemplatesContainerId)

    this.origin = hub.toJSON()
  }

  public goToCreateExist(existId: DataObjectId): void {
    const box = Sync.getCurrentSandbox()
    const existHub = box.getHub(existId)
    if (!existHub) {
      return
    }

    this.state.page = 'create'

    this.state.createFromExist = true

    const hub = new Hub(globals.getCompanyId(), '')
    hub.id = createDataObjectId()
    hub.title = existHub.title
    hub.location = taskTemplatesContainerId
    hub.data = Util.clone(existHub.data)
    hub.description = existHub.description
    hub.stickers = Util.clone(existHub.stickers)
    const tags = Object.keys(hub.stickers)
      .map(sId => Sticker.getById(box, sId as DataObjectId))
      .filter(TypeUtil.isTruthy)

    if (!ObjUtil.isEmpty(hub.data.subtaskList?.subtasks)) {
      Object.keys(hub.data.subtaskList?.subtasks || {}).forEach(subtaskKey => {
        const subtasks = hub.data.subtaskList?.subtasks || {}
        if (subtasks[subtaskKey]) {
          subtasks[subtaskKey] = {
            ...subtasks[subtaskKey],
            location: taskTemplatesContainerId
          }
        }
      })
    }

    const filteredIds = filterCustomStickers(tags, true).map(s => s.id)

    hub.stickers = (Object.keys(hub.stickers) as DataObjectId[]).reduce(
      (acc, stickerId) => {
        if (filteredIds.includes(stickerId)) {
          acc[stickerId] = hub.stickers[stickerId]
        }

        return acc
      },
      {} as {[id: string]: JSONLikeValue}
    )

    const deadlineTag = tags.find(t => t.title === 'Deadline')
    if (deadlineTag) {
      hub.stickers[deadlineTag.id] = ''
    }

    const timerTag = tags.find(t => t.title === 'Timer')
    if (timerTag) {
      const time = hub.stickers[timerTag.id]?.current?.time
      if (time) {
        const entry: TimerStickerEntry = {
          by: globals.currentUserId,
          running: false,
          time,
          timestamp: Time.now()
        }
        hub.stickers[timerTag.id] = {
          current: entry,
          history: [entry]
        }
      } else {
        hub.stickers[timerTag.id] = ''
      }
    }

    const allUsers = box.getAllUsers(false)
    const users = allUsers
      .filter(u => ChatSubscribers.isSubscribed(u.id, existHub))
      .map(u => u.id)

    hub.data.taskTemplate = {
      id: hub.id,
      boardId: this.boardId,
      title: existHub.title,
      enabled: true,
      subscribeUsers: users
    }

    recreateSubtasksForTaskTemplate(hub)

    this.template = hub
    box?.addPhantom(this.template, taskTemplatesContainerId)

    hub.title = 'fix'
    this.origin = hub.toJSON()
    hub.title = existHub.title
  }

  public getTitle(): string | undefined {
    return this.state.title || this.template?.data?.taskTemplate?.title
  }

  public setTitle(value: string): void {
    this.state.title = value

    if (this.template?.data?.taskTemplate) {
      this.template.data.taskTemplate.title = value
    }

    taskPhantomObserver.updateWithDebounce()
  }

  public getSafeTitle(): string {
    return this.template?.data?.taskTemplate?.title || ''
  }

  public goToEdit(hubId: DataObjectId): void {
    this.state.page = 'edit'
    this.state.editId = hubId
    const box = Sync.getCurrentSandbox()
    this.template = box.getHub(hubId)

    if (this.template) {
      this.originSubtask = {}

      updateShiftDeadline(this.template)

      box.addPhantom(this.template, taskTemplatesContainerId)
      this.origin = this.template.toJSON()

      for (const subtaskId of getSubtaskIds(this.template)) {
        const subtask = box.getHub(subtaskId)
        if (!subtask) {
          continue
        }

        box.addPhantom(subtask, taskTemplatesContainerId)
        this.originSubtask[subtaskId] = subtask.toJSON()
      }
    }

    if (!this.template) {
      return this.goToCreate()
    }
  }

  public save(): boolean {
    if (!this.canSubmit()) {
      return true
    }

    const title = this.getSafeTitle().trim()
    const noTitle = !title.length
    if (noTitle) {
      MessageBox.warn(Lang.msg.taskTemplates.onSubmitEmptyTitle)
      return true
    }

    if (this.state.editId) {
      this.saveEditHub()
    } else {
      this.createTemplate()
    }

    this.goToMain()

    setTimeout(() => {
      runInAction(() => {
        boardSettingsStore.setHasChanges(false)
        boardSettingsStore.setOnHasChangesCb(undefined)
      })
    }, 0)

    taskPhantomObserver.update()
    return true
  }

  private createTemplate(): void {
    const box = Sync.getCurrentSandbox()
    if (!box) {
      return
    }

    const board = box.getHub(this.boardId)
    if (!board) {
      return
    }

    const hub = this.template
    if (!hub) {
      return
    }
    box.removePhantom(hub)
    box.add(hub, taskTemplatesContainerId, true, false)

    const subs = hub.data?.subtaskList?.order || []

    for (const hubId of subs) {
      const sub = box.getHub(hubId)

      if (!sub) {
        continue
      }

      box.removePhantom(sub)
      box.add(sub, taskTemplatesContainerId, true, false)
    }

    if (!board.data?.taskTemplates?.includes(hub.id)) {
      board.data ??= {}
      board.data.taskTemplates = board.data.taskTemplates ?? []
      board.data.taskTemplates.unshift(hub.id)
      board.commit()
    }
  }

  private saveEditHub(): void {
    const box = Sync.getCurrentSandbox()
    if (!box || !this.template) {
      return
    }

    const subs = this.template.data?.subtaskList?.order || []
    for (const hubId of subs) {
      const sub = box.getHub(hubId)
      if (!sub) {
        continue
      }

      if (HubUtil.isPhantom(sub)) {
        box.removePhantom(sub)
        box.add(sub, taskTemplatesContainerId, true, false)
      }
    }

    box.removePhantom(this.template)
    box.add(this.template, taskTemplatesContainerId, true, false)
  }

  public canSubmit(): boolean {
    const hasChanges = this.isTemplateChanged()

    setTimeout(() => {
      runInAction(() => {
        boardSettingsStore.setHasChanges(hasChanges)
        const cb = hasChanges ? this.showError : undefined
        boardSettingsStore.setOnHasChangesCb(cb)
      })
    }, 0)

    return hasChanges
  }

  public showError(): void {
    this.state.errorHasChanges = true
    this.state.errorHasChangesBgRed = true
    setTimeout(() => {
      runInAction(() => {
        this.state.errorHasChangesBgRed = false
      })
    }, 300)
  }

  public validToCreate(): boolean {
    const title = this.getTitle() || ''

    const validTitle = title.trim().length >= 1
    return validTitle
  }

  private isTemplateChanged(): boolean {
    if (!this.template) {
      return false
    }

    const diff = Diff.calculate(this.origin, this.template.toJSON())

    const res = !!(diff.minus.length || diff.plus.length)
    if (res) {
      return true
    }

    const box = Sync.getCurrentSandbox()
    for (const subtaskId of getSubtaskIds(this.template)) {
      const subtask = box.getHub(subtaskId)
      if (!subtask) {
        continue
      }

      const origSubtask = this.originSubtask[subtaskId]
      const diffSubs = Diff.calculate(origSubtask, subtask.toJSON())
      const resSubs = !!(diffSubs.minus.length || diffSubs.plus.length)
      if (resSubs) {
        return true
      }
    }

    return false
  }

  public syncRows(): void {
    const list = TaskTemplateClient.listTemplatesInBoard(this.boardId)

    this.state.rows = list
      .map(l => {
        if (!l.data.taskTemplate) {
          return undefined
        }

        const {id, title, enabled} = l.data.taskTemplate

        return {
          id,
          enabled,
          text: title
        }
      })
      .filter(Boolean) as TaskTemplateRow[]
  }
}

const [TaskTemplatesContext, useTaskTemplatesStore] =
  createUseStore(TaskTemplatesStore)
export {TaskTemplatesContext, useTaskTemplatesStore}

const getSubtaskIds = (hub?: Hub): DataObjectId[] => {
  const subtasks = hub?.data?.subtaskList?.subtasks || {}
  return Object.keys(subtasks) as DataObjectId[]
}
